import {
  EnergyFlowBatteryIntervalModel,
  EnergyFlowEmeterIntervalModel,
  EnergyFlowEvChargerIntervalModel,
  EnergyFlowHeatpumpIntervalModel,
  EnergyFlowIntervalModel,
  EnergyFlowModel,
  EnergyFlowPvForecastIntervalModel,
  EnergyFlowSolarIntervalModel,
} from '@core/models/topological-telemetry.model';
import { TranslateService } from '@ngx-translate/core';
import { ChartData, ChartDataset } from 'chart.js';
import { TopologicalDeviceTelemetryStore } from '@root/app/store/topological-device-telemetry.store';
import { ChartRangeOptions } from '@core/models';
import { withAlpha, XemexChartDataset } from '@custom-components/general/chart/chart.component';
import moment from 'moment-timezone';
import { CHART_SERIES_NEUTRAL } from '@core/constants/tokens.generated';
import {
  buildDayHourSlots,
  buildDayQuarterSlots,
  clockChangeOf,
  DaySlots,
  DEFAULT_TIME_ZONE,
  DstSuffixes,
  dstMarkerData,
  FoldableDataset,
  placeOnDaySlots,
  QUARTER_LABEL_MARKER,
} from './day-hour-labels';

export enum ChartType {
  LINE = 'line',
  BAR = 'bar',
}

// Chart data plus an optional explanatory note under and badge above the chart (e.g. on a clock-change day).
export type XemexChartData = ChartData & { note?: string; badge?: string };

export function toChartNote(chartData: XemexChartData | undefined): string | undefined {
  return chartData?.note;
}

export function toChartBadge(chartData: XemexChartData | undefined): string | undefined {
  return chartData?.badge;
}

export function toChartLabels(chartData: { labels?: unknown[] } | undefined): string[] {
  return (chartData?.labels ?? []) as string[];
}

export function toChartDatasets(chartData: { datasets?: any[] } | undefined): XemexChartDataset[] {
  return (chartData?.datasets ?? []).map((ds: any) => ({
    label: ds.label ?? '',
    data: ds.data ?? [],
    type: (ds.type ?? 'line') as 'line' | 'bar',
    color: ds.backgroundColor ?? '',
    ...(ds.yAxisID ? { yAxisID: ds.yAxisID } : {}),
    ...(ds.borderDash ? { borderDash: ds.borderDash } : {}),
    ...(ds.stack ? { stack: ds.stack } : {}),
    ...(ds.dstMarker ? { dstMarker: true } : {}),
  }));
}

export class ChartBuilder {
  private readonly telemetryStore: InstanceType<typeof TopologicalDeviceTelemetryStore>;
  private readonly translateService: TranslateService;
  private data: EnergyFlowModel<EnergyFlowIntervalModel> | null;
  private readonly chartSetup: XemexChartData;
  private daySlots: DaySlots | null = null;
  private intervalLabelsOverride: string[] | null = null;
  private readonly timeZone: string;
  constructor(
    telemetryStore: InstanceType<typeof TopologicalDeviceTelemetryStore>,
    translateService: TranslateService,
    timeZone?: string,
  ) {
    this.telemetryStore = telemetryStore;
    this.translateService = translateService;
    // The day slots, badge and marker follow the zone of the installation, never fixed dates.
    this.timeZone = timeZone ?? telemetryStore.timeZone?.() ?? DEFAULT_TIME_ZONE;
    this.data = null;
    this.chartSetup = {
      datasets: [],
    };
  }

  withChartSetup() {
    switch (this.telemetryStore.deviceChartTimeOption()) {
      case ChartRangeOptions.Year: {
        const yeardata = this.telemetryStore.deviceTopologicalTelemetryYearPerMonthData();
        this.chartSetup.labels = yeardata?.intervals.map(monthInterval => monthInterval.month);
        this.data = yeardata;
        break;
      }
      case ChartRangeOptions.Month: {
        const monthdata = this.telemetryStore.deviceTopologicalTelemetryMonthPerDayData();
        this.data = monthdata;
        this.chartSetup.labels = monthdata?.intervals.map(dayInterval => dayInterval.day);
        break;
      }
      case ChartRangeOptions.Week: {
        const weekdata = this.telemetryStore.deviceTopologicalTelemetryWeekPerDayData();
        this.data = weekdata;
        this.chartSetup.labels = [
          this.translateService.instant('devices.detailTabs.insights.monday'),
          this.translateService.instant('devices.detailTabs.insights.tuesday'),
          this.translateService.instant('devices.detailTabs.insights.wednesday'),
          this.translateService.instant('devices.detailTabs.insights.thursday'),
          this.translateService.instant('devices.detailTabs.insights.friday'),
          this.translateService.instant('devices.detailTabs.insights.saturday'),
          this.translateService.instant('devices.detailTabs.insights.sunday'),
        ];
        break;
      }
      case ChartRangeOptions.Day: {
        const dayData = this.telemetryStore.deviceTopologicalTelemetryDayPerHourData();
        this.data = dayData;
        const referenceDate =
          this.telemetryStore.selectedDate().split('/')[1] ?? moment().startOf('day');
        const slots = buildDayHourSlots(
          referenceDate,
          (dayData?.intervals ?? []).map(d => d.hour),
          this.dstSuffixes,
          this.timeZone,
        );
        this.intervalLabelsOverride = slots.intervalLabels;
        this.chartSetup.labels = slots.labels;
        this.daySlots = slots;
        this.chartSetup.badge = this.clockChangeBadge(referenceDate);
        break;
      }
      case ChartRangeOptions.DayPerQuarter: {
        const dayPerQuarterData =
          this.telemetryStore.deviceTopologicalTelemetryDayPerQuarterHourData();
        this.data = dayPerQuarterData;
        const referenceDate =
          this.telemetryStore.selectedDate().split('/')[1] ?? moment().startOf('day');
        const slots = buildDayQuarterSlots(
          referenceDate,
          (dayPerQuarterData?.intervals ?? []).map(d => d.quarter_hour),
          this.dstSuffixes,
          this.timeZone,
        );
        this.chartSetup.labels = slots.labels.map(label => QUARTER_LABEL_MARKER + label);
        this.intervalLabelsOverride = slots.intervalLabels;
        this.daySlots = slots;
        this.chartSetup.badge = this.clockChangeBadge(referenceDate);
        break;
      }
    }
    return this;
  }

  withData(
    chartType: ChartType,
    data: (d: EnergyFlowIntervalModel) => number | null,
    label: string,
    color: string,
  ) {
    if (!this.data) return this;
    this.chartSetup.datasets.push({
      type: chartType,
      data: this.data.intervals.map(data),
      label: this.translateService.instant(label),
      backgroundColor: color,
      borderColor: color,
    });
    return this;
  }

  withChannelData(
    chartType: ChartType,
    data: (d: EnergyFlowIntervalModel, channelNumber: number) => number | null,
    selector: (
      i: EnergyFlowIntervalModel,
    ) =>
      | EnergyFlowHeatpumpIntervalModel[]
      | EnergyFlowEmeterIntervalModel[]
      | EnergyFlowSolarIntervalModel[]
      | EnergyFlowBatteryIntervalModel[]
      | EnergyFlowEvChargerIntervalModel[]
      | EnergyFlowPvForecastIntervalModel[],
    label:
      | ((
          i: EnergyFlowIntervalModel | undefined,
          channelNumber: number,
          channelCount: number,
        ) => string)
      | string,
    color: string | string[],
    altAxis: string | null = null,
    borderDash: number[] | null = null,
  ) {
    if (!this.data) return this;

    const channelNumbers = [
      ...new Set(this.data.intervals.flatMap(i => selector(i).flatMap(h => h.channelNumber))),
    ];
    for (const channelNumber of channelNumbers) {
      this.chartSetup.datasets.push({
        type: chartType,
        data: this.data.intervals.map(x => data(x, channelNumber)),
        label:
          typeof label == 'string'
            ? this.translateService.instant(label)
            : label(this.data.intervals.at(0), channelNumber, channelNumbers.length),
        backgroundColor: Array.isArray(color) ? this.selectColor(color, channelNumber) : color,
        yAxisID: altAxis ?? 'y',
        borderColor: Array.isArray(color) ? this.selectColor(color, channelNumber) : color,
        ...(borderDash ? { borderDash } : {}),
      });
    }
    return this;
  }

  // A day chart always has 24 hour or 96 quarter positions. On the 25-hour day datasets for which
  // `stackable` holds (amounts) show the summer- and winter-time 02:00 as one stacked bar, the others show
  // the summer-time value; on the 23-hour day the skipped hour gets a daylight saving marker.
  build(stackable: (dataset: ChartDataset) => boolean = () => false): XemexChartData {
    this.chartSetup.datasets = this.fold(
      this.chartSetup.datasets,
      stackable,
      (ds, part) => {
        const color =
          part === 'winter' && typeof ds.backgroundColor === 'string'
            ? withAlpha(ds.backgroundColor, 0.45)
            : ds.backgroundColor;
        return { type: 'bar', backgroundColor: color, borderColor: color } as Partial<ChartDataset>;
      },
      (label, data) =>
        ({
          type: 'line',
          label,
          data,
          backgroundColor: CHART_SERIES_NEUTRAL,
          borderColor: CHART_SERIES_NEUTRAL,
          dstMarker: true,
        }) as ChartDataset,
    );
    return this.chartSetup;
  }

  // Same placement for charts that build their datasets directly from chartIntervals.
  foldRepeatedHour(
    datasets: XemexChartDataset[],
    stackable: (dataset: XemexChartDataset) => boolean = () => false,
  ): XemexChartDataset[] {
    return this.fold(
      datasets,
      stackable,
      (ds, part) => ({
        type: 'bar',
        color: part === 'winter' ? withAlpha(ds.color, 0.45) : ds.color,
      }),
      (label, data) => ({
        type: 'line',
        label,
        data,
        color: CHART_SERIES_NEUTRAL,
        dstMarker: true,
      }),
    );
  }

  get chartNote(): string | undefined {
    return this.chartSetup.note;
  }

  get chartBadge(): string | undefined {
    return this.chartSetup.badge;
  }

  private fold<T extends FoldableDataset>(
    datasets: T[],
    stackable: (dataset: T) => boolean,
    barStyle: (dataset: T, part: 'summer' | 'winter') => Partial<T>,
    marker: (label: string, data: (number | null)[]) => T,
  ): T[] {
    const slots = this.daySlots;
    if (!slots) return datasets;
    if (slots.repeated.length > 0) {
      const stacked = datasets.some(ds => !!ds.label && stackable(ds));
      this.chartSetup.note = this.translateService.instant(
        stacked
          ? 'devices.detailTabs.insights.clockChangeNote'
          : 'devices.detailTabs.insights.clockChangeNoteUnstacked',
      );
    }
    const placed = placeOnDaySlots(
      datasets,
      slots,
      this.dstSuffixes.winterTime,
      barStyle,
      stackable,
    );
    if (slots.skipped.length === 0 || datasets.length === 0) return placed;
    const tooltip = this.translateService.instant('devices.detailTabs.insights.dstMarkerTooltip');
    return [...placed, marker(tooltip, dstMarkerData(slots))];
  }

  private clockChangeBadge(referenceDate: moment.MomentInput): string | undefined {
    const change = clockChangeOf(referenceDate, this.timeZone);
    if (!change) return undefined;
    return this.translateService.instant('devices.detailTabs.insights.clockChangeBadge', {
      zone: change.zone,
      transition: this.translateService.instant(
        change.transition === 'forward'
          ? 'devices.detailTabs.insights.clocksForward'
          : 'devices.detailTabs.insights.clocksBack',
      ),
      hours: change.hours,
    });
  }

  private get dstSuffixes(): DstSuffixes {
    return {
      summerTime: this.translateService.instant('devices.detailTabs.insights.summerTime'),
      winterTime: this.translateService.instant('devices.detailTabs.insights.winterTime'),
    };
  }

  // Time-axis labels produced by withChartSetup(), reused by charts that build
  // their datasets directly instead of through withData/withChannelData.
  get chartLabels(): string[] {
    return (this.chartSetup.labels ?? []) as string[];
  }

  // One label per interval for tables: equal to chartLabels, except on the 25-hour day where a table
  // tells the repeated 02:00 (and its quarters) apart by summer/winter time, and the hour chart folds
  // them into one slot.
  get intervalLabels(): string[] {
    return this.intervalLabelsOverride ?? this.chartLabels;
  }

  // Intervals for the currently selected time range, in the same order as the labels.
  get chartIntervals(): EnergyFlowIntervalModel[] {
    return this.data?.intervals ?? [];
  }

  selectColor(colors: string[], channel: number) {
    return colors[channel % colors.length];
  }
}
